import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, Box, Text, HStack, Badge, VStack, Flex, Spinner,
} from '@chakra-ui/react';
import { FiGitBranch, FiCheckCircle, FiClock, FiPauseCircle } from 'react-icons/fi';
import api from '../api';

function fmtDate(d) {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.split('T')[0] : d;
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusIcon({ task }) {
  if (task.is_complete) return <FiCheckCircle color="#38A169" size={14} />;
  if (task.is_held)     return <FiPauseCircle color="#E53E3E" size={14} />;
  return <FiClock color="#718096" size={14} />;
}

function StatusBadge({ task }) {
  if (task.is_complete) return <Badge colorScheme="green" fontSize="8px">Complete</Badge>;
  if (task.is_held)     return <Badge colorScheme="red"   fontSize="8px">Held</Badge>;
  return <Badge colorScheme="gray" fontSize="8px">In Progress</Badge>;
}

export default function TaskCloneHistoryModal({ task, isOpen, onClose }) {
  const [clones, setClones]   = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !task) return;
    setLoading(true);
    api.get(`/tasks/${task.id}/clones`)
      .then(({ data }) => setClones(data))
      .catch(() => setClones([]))
      .finally(() => setLoading(false));
  }, [isOpen, task]);

  if (!task) return null;

  const rootId = task.source_task_id || task.id;
  const currentId = task.id;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader fontSize="md">
          <HStack spacing={2}>
            <FiGitBranch size={16} />
            <Text>Clone History</Text>
          </HStack>
          <Text fontSize="11px" fontWeight="400" color="gray.500" mt={0.5}>
            All instances of "{task.task_title}"
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Flex justify="center" py={8}><Spinner color="blue.500" /></Flex>
          ) : clones.length === 0 ? (
            <Text fontSize="12px" color="gray.400" textAlign="center" py={8}>No clone history found.</Text>
          ) : (
            <VStack spacing={0} align="stretch">
              {clones.map((c, idx) => {
                const isOriginal = !c.source_task_id;
                const isCurrent  = c.id === currentId;
                const isLast     = idx === clones.length - 1;

                return (
                  <Flex key={c.id} align="stretch">
                    {/* Timeline spine */}
                    <Box w="32px" flexShrink={0} display="flex" flexDirection="column" alignItems="center">
                      <Box
                        w="2px" flex={1} bg={isLast ? 'transparent' : 'blue.200'}
                        minH={idx === 0 ? '16px' : '0'}
                      />
                      <Box
                        w="12px" h="12px" borderRadius="full" flexShrink={0}
                        bg={isCurrent ? 'blue.500' : c.is_complete ? 'green.400' : c.is_held ? 'red.400' : 'gray.300'}
                        border="2px solid"
                        borderColor={isCurrent ? 'blue.300' : c.is_complete ? 'green.200' : 'gray.200'}
                        zIndex={1}
                      />
                      <Box w="2px" flex={1} bg={isLast ? 'transparent' : 'blue.200'} />
                    </Box>

                    {/* Card */}
                    <Box
                      flex={1} ml={2} mb={3}
                      p={3} border="1px" borderRadius="md"
                      borderColor={isCurrent ? 'blue.300' : 'gray.200'}
                      bg={isCurrent ? 'blue.50' : 'white'}
                      boxShadow={isCurrent ? 'sm' : 'none'}
                    >
                      <Flex justify="space-between" align="flex-start">
                        <Box flex={1}>
                          <HStack spacing={2} mb={0.5} flexWrap="wrap">
                            <HStack spacing={1}>
                              <StatusIcon task={c} />
                              <Text fontSize="12px" fontWeight="700" color={isCurrent ? 'blue.700' : 'gray.800'}>
                                {c.task_title || 'Untitled'}
                              </Text>
                            </HStack>
                            {isOriginal && (
                              <Badge colorScheme="orange" fontSize="8px" variant="subtle">Original</Badge>
                            )}
                            {isCurrent && (
                              <Badge colorScheme="blue" fontSize="8px">Current</Badge>
                            )}
                            <StatusBadge task={c} />
                          </HStack>

                          {c.description && (
                            <Text fontSize="10px" color="gray.500" noOfLines={2} mb={1}>{c.description}</Text>
                          )}

                          <HStack spacing={3} fontSize="10px" color="gray.400" flexWrap="wrap">
                            {c.start_date && (
                              <Text>Start: <b>{fmtDate(c.start_date)}</b></Text>
                            )}
                            {c.deadline_date && (
                              <Text>Deadline: <b>{fmtDate(c.deadline_date)}</b></Text>
                            )}
                            {c.created_at && (
                              <Text>Created: <b>{fmtDate(c.created_at)}</b></Text>
                            )}
                            {c.created_by_name && (
                              <Text>by <b>{c.created_by_name}</b></Text>
                            )}
                          </HStack>
                        </Box>

                        <Box textAlign="right" ml={3} flexShrink={0}>
                          <Text fontSize="9px" color="gray.400" mb={0.5}>#{c.id}</Text>
                          {!!c.is_complete && c.deadline_date && (
                            <Badge colorScheme="green" fontSize="8px">Done {fmtDate(c.deadline_date)}</Badge>
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} mr="auto" fontSize="10px" color="gray.400">
            <Box w="10px" h="10px" borderRadius="full" bg="green.400" />
            <Text>Complete</Text>
            <Box w="10px" h="10px" borderRadius="full" bg="gray.300" />
            <Text>In Progress</Text>
            <Box w="10px" h="10px" borderRadius="full" bg="blue.500" />
            <Text>Current</Text>
          </HStack>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
